import React, {
  memo,
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import { Grid, Box } from '@mui/material';
import { useFormik } from 'formik';
import FormTextField from './components/FormTextField';
import FormPhoneField from './components/FormPhoneField';
import { generateContactSchema } from './components/validationSchema';
import { PHONE_CODES } from 'legacyfrontend/constants/countries';

const fieldMapping = {
  name: 'nombre',
  surname: 'apellidos',
  email: 'email',
  phone: 'telefono1',
  phone2: 'telefono2',
};

const ContactForm = forwardRef(({ onSubmit, contactConfig, updateContactInfo }, ref) => {
  const { fields = [], requiredFields = [], mandatory = false } = contactConfig || {};
  const hasPhone2 = fields.some((field) => field.name === 'phone2');

  const formik = useFormik({
    initialValues: {
      nombre: '',
      apellidos: '',
      email: '',
      telefono1_codigo: '+54',
      telefono1_numero: '',
      ...(hasPhone2 && {
        telefono2_codigo: '+54',
        telefono2_numero: '',
      }),
    },
    validationSchema: generateContactSchema(contactConfig),
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      formik.setFieldValue(name, value);
    },
    [formik]
  );

  const handleBlur = useCallback(
    (e) => {
      const { name } = e.target;
      formik.setFieldTouched(name, true);
    },
    [formik]
  );

  const handleSubmit = useCallback(() => {
    const { values } = formik;
    const formattedValues = {
      nombre: values.nombre,
      apellidos: values.apellidos,
      email: values.email,
      telefono1: {
        codigo: values.telefono1_codigo,
        numero: values.telefono1_numero,
      },
      ...(hasPhone2 && {
        telefono2: {
          codigo: values.telefono2_codigo,
          numero: values.telefono2_numero,
        },
      }),
    };
    onSubmit(formattedValues);
  }, [formik.values, onSubmit, hasPhone2]);

  useImperativeHandle(ref, () => ({
    handleSubmit,
    getValues: () => formik.values,
    getErrors: () => formik.errors,
    validate: async () => {
      const errors = await formik.validateForm();
      return Object.keys(errors).length === 0;
    },
  }));

  useEffect(() => {
    if (updateContactInfo) {
      updateContactInfo(formik.values);
    }
  }, [formik.values, updateContactInfo]);

  return (
    <Box sx={{ p: 3, pt: 1, bgcolor: 'white', borderRadius: 1 }}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          {fields.map((field) => {
            const fieldName = fieldMapping[field.name];
            if (!fieldName) return null;

            const isRequired = requiredFields.includes(field.name);

            if (fieldName === 'nombre') {
              return (
                <Grid item key={fieldName} md={4} xs={12}>
                  <FormTextField
                    error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
                    formik={formik}
                    helperText={formik.touched[fieldName] && formik.errors[fieldName]}
                    inputType='text'
                    label='Nombre'
                    name={fieldName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder='Ingrese el nombre'
                    required={isRequired}
                  />
                </Grid>
              );
            }

            if (fieldName === 'apellidos') {
              return (
                <Grid item key={fieldName} md={4} xs={12}>
                  <FormTextField
                    FormHelperTextProps={{
                      sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      },
                    }}
                    error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
                    formik={formik}
                    helperText={
                      <>
                        {41 - (formik.values[fieldName]?.length || 0)} caracteres restantes
                        {formik.touched[fieldName] && formik.errors[fieldName] && (
                          <span
                            style={{
                              color: 'error.main',
                              display: 'block',
                            }}
                          >
                            {formik.errors[fieldName]}
                          </span>
                        )}
                      </>
                    }
                    inputProps={{ maxLength: 41 }}
                    inputType='text'
                    label='Apellidos'
                    name={fieldName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder='Ingrese los apellidos'
                    required={isRequired}
                  />
                </Grid>
              );
            }

            if (fieldName === 'email') {
              return (
                <Grid item key={fieldName} md={4} xs={12}>
                  <FormTextField
                    error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
                    formik={formik}
                    helperText={formik.touched[fieldName] && formik.errors[fieldName]}
                    label='Email'
                    name={fieldName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder='Ingrese su email'
                    required={isRequired}
                  />
                </Grid>
              );
            }

            if (fieldName === 'telefono1') {
              return (
                <Grid item key={fieldName} md={6} xs={12}>
                  <FormPhoneField
                    codigosPais={PHONE_CODES}
                    error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
                    formik={formik}
                    helperText={formik.touched[fieldName] && formik.errors[fieldName]}
                    label='Teléfono'
                    name='telefono1'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required={isRequired}
                  />
                </Grid>
              );
            }

            if (fieldName === 'telefono2' && hasPhone2) {
              return (
                <Grid item key={fieldName} md={6} xs={12}>
                  <FormPhoneField
                    codigosPais={PHONE_CODES}
                    error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
                    formik={formik}
                    helperText={formik.touched[fieldName] && formik.errors[fieldName]}
                    label='Teléfono alternativo'
                    name='telefono2'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required={isRequired}
                  />
                </Grid>
              );
            }

            return null;
          })}
        </Grid>
      </form>
    </Box>
  );
});

ContactForm.displayName = 'ContactForm';

export default memo(ContactForm);
